import type { XY } from "../engine/xy";
import { TextPopupEffect } from "../engine/effects/textPopupEffect";
import { Strings } from "../engine/strings";
import type { Camera } from "./camera";
import { dpr } from "./dpr";
import { FloatingPopupRenderer, type FloatingPopupBase } from "./floatingPopupRenderer";

export const TEXT_POPUP_DURATION_MS = 1100;
const POPUP_FADE_START = 0.8;
const POPUP_FLOAT_DISTANCE = 26;
const POPUP_INITIAL_Y_OFFSET = -22; // negative = above the tile
const POPUP_FONT_SIZE = 12;
const POPUP_BG_COLOR = "rgba(0, 0, 0, 0.3)";
const POPUP_DEFAULT_TEXT_COLOR = "#ffffff";
const POPUP_PADDING_X = 5;
const POPUP_PADDING_Y = 2;

interface ActivePopup extends FloatingPopupBase {
	anchorXY: XY;
	text: string;
	color: string;
}

// Draws any TextPopupEffect; same anchor + same text refreshes in place rather than stacking.
export class TextPopupRenderer extends FloatingPopupRenderer<ActivePopup> {
	protected durationMs = TEXT_POPUP_DURATION_MS;
	protected fadeStart = POPUP_FADE_START;
	protected floatDistance = POPUP_FLOAT_DISTANCE;

	addPopup(effect: TextPopupEffect, camera: Camera): void {
		// Localize here, in the UI layer where Strings is initialized; unknown keys render as-is.
		const text = Strings.get(effect.textKey, ...effect.params) ?? effect.textKey;

		const existing = this.findActivePopupAt(effect.xy, text);
		if (existing) {
			existing.elapsed = 0;
			this.snapToCamera(existing, camera);
			return;
		}

		const popup: ActivePopup = {
			anchorXY: effect.xy,
			text,
			color: effect.color ?? POPUP_DEFAULT_TEXT_COLOR,
			elapsed: 0,
			screenX: 0,
			screenY: 0,
		};
		this.snapToCamera(popup, camera);
		this.popups.push(popup);
	}

	private findActivePopupAt(xy: XY, text: string): ActivePopup | undefined {
		return this.popups.find(
			(p) => p.anchorXY.equals(xy) && p.text === text && p.elapsed < this.durationMs,
		);
	}

	private snapToCamera(popup: ActivePopup, camera: Camera): void {
		const center = camera.getCellCenterPixel(popup.anchorXY);
		popup.screenX = center.x;
		popup.screenY = center.y - camera.cellHeight / 2 + POPUP_INITIAL_Y_OFFSET * dpr();
	}

	protected drawContent(
		ctx: CanvasRenderingContext2D,
		popup: ActivePopup,
		yOffset: number,
		ratio: number,
	): void {
		const fontSize = Math.round(POPUP_FONT_SIZE * ratio);
		const paddingX = POPUP_PADDING_X * ratio;
		const paddingY = POPUP_PADDING_Y * ratio;

		ctx.font = `${fontSize}px monospace`;

		const textWidth = ctx.measureText(popup.text).width;

		const bgWidth = textWidth + paddingX * 2;
		const bgHeight = fontSize + paddingY * 2;
		const bgX = popup.screenX - bgWidth / 2;
		const bgY = popup.screenY + yOffset - bgHeight / 2;

		ctx.fillStyle = POPUP_BG_COLOR;
		ctx.fillRect(bgX, bgY, bgWidth, bgHeight);

		ctx.fillStyle = popup.color;
		ctx.fillText(popup.text, bgX + paddingX, bgY + paddingY + fontSize);
	}

	// Test helpers.
	hasActivePopupAt(xy: XY): boolean {
		return this.popups.some((p) => p.anchorXY.equals(xy) && p.elapsed < this.durationMs);
	}

	getActivePopupText(xy: XY): string | undefined {
		return this.popups.find((p) => p.anchorXY.equals(xy) && p.elapsed < this.durationMs)?.text;
	}

	getPopupCount(): number {
		return this.popups.length;
	}
}
